import { useEffect, useMemo, useRef } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { buildProductDescriptor } from "~/domains/pna/descriptor";
import { refreshProductAvailability } from "~/domains/pna/pnaCommands";
import { pnaAvailabilityQueryOptions } from "~/domains/pna/pnaQueries";
import { toAvailabilityOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import type { PackagingInformationEntry, ProductData } from "~/domains/product/types/ProductData";
import type {
  UseEffectivePackagingParams,
  UseEffectivePackagingResult,
} from "~/domains/product/types/EffectivePackaging";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  buildEffectivePackaging,
  buildSimpleEffectivePackagingEntry,
  type PredecessorDescriptorWithEntries,
} from "~/domains/product/utils/buildEffectivePackaging";

export interface UseEffectivePackagingResolvedResult extends UseEffectivePackagingResult {
  predecessors: PredecessorDescriptorWithEntries[];
}

function normalizePriceReference(priceReference: string) {
  return priceReference === "-" ? "" : priceReference;
}

function getActiveEntries(entries: PackagingInformationEntry[], restrictionPackCode?: string) {
  if (!restrictionPackCode) {
    return entries;
  }

  const filteredEntries = entries.filter((entry) => entry.packCode === restrictionPackCode);
  return filteredEntries.length > 0 ? filteredEntries : entries;
}

function resolveCrossProductPredecessors(
  descriptor: ProductDescriptor,
  activeEntries: PackagingInformationEntry[],
): ProductData[] {
  const mainProduct = descriptor.base;
  const predecessorProductsBySapMaterialNumber = new Map(
    mainProduct.predecessorProducts.map((product) => [product.sapMaterialNumber, product]),
  );
  const crossProductsByCode = new Map<string, ProductData>();

  activeEntries.forEach((entry) => {
    const fifteenDigitEntry = mainProduct.fifteenDigit[entry.packCode];

    if (!fifteenDigitEntry) {
      return;
    }

    if (fifteenDigitEntry.successor.slice(0, 13) === mainProduct.sapMaterialNumber) {
      return;
    }

    fifteenDigitEntry.predecessors.forEach((materialNo15) => {
      const sourceSapMaterialNumber = materialNo15.slice(0, 13);

      if (sourceSapMaterialNumber === mainProduct.sapMaterialNumber) {
        return;
      }

      const sourceProduct = predecessorProductsBySapMaterialNumber.get(sourceSapMaterialNumber);
      if (sourceProduct) {
        crossProductsByCode.set(sourceProduct.code, sourceProduct);
      }
    });
  });

  return Array.from(crossProductsByCode.values());
}

export function useEffectivePackaging({
  descriptor,
  priceReference,
  restrictionPackCode,
}: UseEffectivePackagingParams): UseEffectivePackagingResolvedResult {
  const queryClient = useQueryClient();
  const normalizedPriceReference = normalizePriceReference(priceReference);
  const isPhaseoutEnabled = isPhaseOut2Enabled();

  const activeEntries = useMemo(
    () => getActiveEntries(descriptor?.availability?.packagingInformation ?? [], restrictionPackCode),
    [descriptor?.availability?.packagingInformation, restrictionPackCode],
  );

  const crossProductPredecessors = useMemo(() => {
    if (!descriptor || !isPhaseoutEnabled || !descriptor.availability) {
      return [];
    }

    return resolveCrossProductPredecessors(descriptor, activeEntries);
  }, [descriptor, activeEntries, isPhaseoutEnabled]);

  // Cross-product predecessor availability lives in the canonical PnA queries (task 0042, A05
  // slice 2): the subscription also performs the initial recovery load the legacy forced Redux
  // dispatch used to trigger. Predecessor prices are never fetched here, so the price outcome
  // projects the legacy absent-entry shape (`prices: []`, no loading/error flags).
  const availabilityOptions = crossProductPredecessors.map((product) =>
    pnaAvailabilityQueryOptions({ productCode: product.code }),
  );
  const availabilityQueries = useQueries({ queries: availabilityOptions });

  // Rebuilt per render (useQueries yields fresh result proxies each render, so memoization
  // cannot hold); harmless — downstream memos/effect churn is absorbed by the recovery guard.
  const crossProductDescriptors: ProductDescriptor[] = crossProductPredecessors.map((product, index) =>
    buildProductDescriptor(
      product,
      {
        price: { status: "disabled" },
        availability: toAvailabilityOutcomeFromQuery(
          availabilityQueries[index],
          availabilityOptions[index].enabled === true,
        ),
      },
      normalizedPriceReference,
    ),
  );

  const refreshedPredecessorCodesRef = useRef<Set<string>>(new Set());

  // One-time recovery for predecessors whose availability settled without data (the legacy
  // forced dispatch for `!availability && !loading && !error` entries). The gate maps the legacy
  // filter exactly: pending and errored queries are excluded (no retry loop), and a settled
  // loaded-empty query recovers at most once per view — the ref guard replaces the legacy
  // loading-entry exclusion a repeated sentinel result could no longer provide.
  // Accepted delta vs legacy: legacy re-forced a still-omitted predecessor on EVERY store
  // churn (its effect re-ran per notification); this view recovers exactly once per mount.
  // Remount parity holds (fresh ref ≡ legacy's fresh dictionary pass).
  useEffect(() => {
    if (!descriptor || !isPhaseoutEnabled) {
      return;
    }

    crossProductDescriptors.forEach((crossProductDescriptor) => {
      if (
        crossProductDescriptor.availability ||
        crossProductDescriptor.isAvailabilityLoading ||
        crossProductDescriptor.isAvailabilityError ||
        refreshedPredecessorCodesRef.current.has(crossProductDescriptor.base.code)
      ) {
        return;
      }

      refreshedPredecessorCodesRef.current.add(crossProductDescriptor.base.code);
      refreshProductAvailability(queryClient, {
        productCode: crossProductDescriptor.base.code,
      }).catch(() => undefined);
    });
  }, [descriptor, queryClient, crossProductDescriptors, isPhaseoutEnabled]);

  const status = (() => {
    if (!descriptor) {
      return "idle";
    }

    if (descriptor.isAvailabilityError || crossProductDescriptors.some((item) => item?.isAvailabilityError)) {
      return "error";
    }

    if (!descriptor.availability || descriptor.isAvailabilityLoading) {
      return "loading";
    }

    if (
      isPhaseoutEnabled &&
      crossProductDescriptors.some((item) => item?.isAvailabilityLoading || !item?.availability)
    ) {
      return "loading";
    }

    return "loaded";
  })();

  const predecessors = useMemo<PredecessorDescriptorWithEntries[]>(() => {
    if (!descriptor?.availability) {
      return [];
    }

    if (!isPhaseoutEnabled) {
      return [];
    }

    return [
      {
        descriptor,
        entries: descriptor.availability.packagingInformation,
      },
      ...crossProductDescriptors.flatMap((crossDescriptor) => {
        if (!crossDescriptor?.availability) {
          return [];
        }

        return {
          descriptor: crossDescriptor,
          entries: crossDescriptor.availability.packagingInformation,
        };
      }),
    ];
  }, [crossProductDescriptors, descriptor, isPhaseoutEnabled]);

  const entries = useMemo(() => {
    if (!descriptor?.availability || status !== "loaded") {
      return [];
    }

    if (!isPhaseoutEnabled) {
      return activeEntries.map((entry) => buildSimpleEffectivePackagingEntry(descriptor, entry));
    }

    return buildEffectivePackaging(activeEntries, descriptor, predecessors);
  }, [activeEntries, descriptor, isPhaseoutEnabled, predecessors, status]);

  const byPackCode = useMemo(() => {
    return entries.reduce<Record<string, (typeof entries)[number]>>((acc, entry) => {
      acc[entry.packCode] = entry;
      return acc;
    }, {});
  }, [entries]);

  return {
    status,
    entries,
    byPackCode,
    predecessors,
  };
}
